import { Box, Button, Divider, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { Loader, CheckboxListTable, SMBSelect } from '../../../components';
import { IDropdownResponse } from '../../../models';
import { createBulkStandardFeatures } from '../../../fetch';
import { getAdminLookupNeighborhoods, getAdminLookupPlanStylesForSelectedNeighborhoods, getAdminLookupStandardFeatures, getAdminLookupStandardFeatureSetSelections } from '../../../fetch/lookups';
import { Save } from '@mui/icons-material';
import { alphaSort } from '../../../helpers';

const BulkStandardFeaturesSchema = Yup.object().shape({
})

export const BulkStandardFeaturesForm = () => {

    const { enqueueSnackbar } = useSnackbar();
    const neighborhoodActiveFilter = true;    

    const [selectedStandardFeature, setSelectedStandardFeature] = useState<string>('');
    const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<IDropdownResponse[]>([]);
    const [selectedPlanStyles, setSelectedPlanStyles] = useState<IDropdownResponse[]>([]);

    const [neighborhoodFilter, setNeighborhoodFilter] = useState<string>('');
    const [filteredNeighborhoods, setFilteredNeighborhoods] = useState<IDropdownResponse[]>([]);

    const { isLoading: isLoadingStandardFeatures, data: standardFeatures } = useQuery<IDropdownResponse[], Error>(
        ['standardFeatures'],
        () => getAdminLookupStandardFeatures(),
        {
            onSuccess: d => {
                alphaSort(d, 'description');                
            }
        }
    );

    const { isLoading: isLoadingNeighborhoods, data: neighborhoods } = useQuery<IDropdownResponse[], Error>(
        ['neighborhoods'],
        () => getAdminLookupNeighborhoods(neighborhoodActiveFilter),
        {
            onSuccess: d => {
                alphaSort(d, 'description');
                setFilteredNeighborhoods(d);
            }
        }
    );

    let [isLoadingPlanStyles, setIsLoadingPlanStyles] = useState<boolean>(false);
    let [planStyles, setPlanStyles] = useState<IDropdownResponse[]>([]);

    let [isLoadingStandardFeatureSelection, setIsLoadingStandardFeatureSelection] = useState<boolean>(false);
    let [standardFeatureSelections, setStandardFeatureSelections] = useState<IDropdownResponse[]>([]);

    function getIntValues(dropdownResponses: IDropdownResponse[]) {
        const selectedValues = new Array<number>();

        for (var i = 0; i < dropdownResponses.length; i++) {
            var value = parseInt(dropdownResponses[i].value);
            selectedValues.push(value);
        }

        return selectedValues;
    }

    function getStringValues(dropdownResponses: IDropdownResponse[]) {
        const selectedValues = [];

        for (var i = 0; i < dropdownResponses.length; i++) {
            var value = dropdownResponses[i].value;
            selectedValues.push(value);
        }

        return selectedValues;
    }

    useEffect(() => {

        if (!neighborhoodFilter) {
            // If there is no filter specified, we simply revert back to the full list
            setFilteredNeighborhoods(neighborhoods);
            return;
        }

        let listOfFilteredNeighborhoods = new Array<IDropdownResponse>();

        neighborhoods.forEach(value => {
            if (value.description.toLowerCase().includes(neighborhoodFilter.toLowerCase())) {
                listOfFilteredNeighborhoods.push(value);
            }
        });

        setFilteredNeighborhoods(listOfFilteredNeighborhoods);

    }, [neighborhoodFilter, neighborhoods]);

    useEffect(() => {
        
        const fetchPlanStyles = async () => {
            const selectedNeighborhoodIds = getIntValues(selectedNeighborhoods);
            const postData = { neighborhoodIds: selectedNeighborhoodIds };
            setIsLoadingPlanStyles(true);
            setPlanStyles(await getAdminLookupPlanStylesForSelectedNeighborhoods(postData));
            setIsLoadingPlanStyles(false);
        }        
        fetchPlanStyles();
    }, [selectedNeighborhoods]);

    useEffect(() => {
        const fetchStandardFeatureSelections = async () => {
            setIsLoadingStandardFeatureSelection(true);

            if (selectedStandardFeature === '') {
                setStandardFeatureSelections([]);
            }
            else if (selectedStandardFeature.startsWith('SA|')) {
                const trueDropdownOption: IDropdownResponse = { value: "True", description: "True", shorthand: "True" };
                const falseDropdownOption: IDropdownResponse = { value: "False", description: "False", shorthand: "False" };
                const trueFalseDropdownOptions = new Array<IDropdownResponse>();

                trueFalseDropdownOptions.push(trueDropdownOption);
                trueFalseDropdownOptions.push(falseDropdownOption);

                setStandardFeatureSelections(trueFalseDropdownOptions);
            }
            else {
                let optionSetId = Number(selectedStandardFeature);
                setStandardFeatureSelections(await getAdminLookupStandardFeatureSetSelections(optionSetId));                
            }

            setIsLoadingStandardFeatureSelection(false);
        }
        fetchStandardFeatureSelections();
    }, [selectedStandardFeature]);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                standardFeature: '',
                standardFeatureSelection: '',
                selectedNeighborhoods: [],
                selectedPlanStyles: []                
            }}
            validationSchema={BulkStandardFeaturesSchema}
            onSubmit={async (values, actions) => {
                try {
                    values.selectedNeighborhoods = getIntValues(selectedNeighborhoods);
                    values.selectedPlanStyles = getStringValues(selectedPlanStyles);
                    const valuesAdded = await createBulkStandardFeatures(values);
                    if (valuesAdded > 0) {
                        enqueueSnackbar(valuesAdded + ` Standard Feature Selections updated!`, {
                            variant: 'success'
                        });
                    }
                    else {
                        enqueueSnackbar(`No Neighborhood Series found for the provided selections or no updates were needed.`, {
                            variant: 'info'
                        });
                    }
                    setSelectedStandardFeature('');
                    setStandardFeatureSelections([]);
                    setNeighborhoodFilter('');
                    setSelectedNeighborhoods([]);
                    setSelectedPlanStyles([]);
                    actions.resetForm();
                } catch (error: any) {
                    enqueueSnackbar(error?.Detail ?? 'Error Adding Standard Feature Selections, please try again.', {
                        variant: 'error'
                    });
                }
            }}
        >
            {({
                isSubmitting,
                values,
                initialValues,
                setFieldValue,
                handleSubmit,
                dirty,
                isValid,
                handleBlur,
                errors,
                touched
            }) => {
                return (
                    <Form onSubmit={handleSubmit}>
                        {isSubmitting && <Loader type="overlay" position="centered" title="Submitting..." />}

                        <Grid container spacing={1}>
                            <Grid item xs={12} lg={8}>
                                <Typography>
                                    This form will allows you to select a standard feature and apply that to all Neighborhood Series in the selected Neighborhoods where the
                                    series contains one of the selected Plan Styles.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6} lg={5}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <SMBSelect
                                            variant="outlined"
                                            required
                                            onChange={e => {
                                                setFieldValue('standardFeature', e.target.value);
                                                setSelectedStandardFeature(e.target.value);
                                            }}
                                            handleClear={() => {
                                                setFieldValue('standardFeature', '');
                                                setSelectedStandardFeature('');
                                            }}
                                            onBlur={handleBlur}
                                            disabled={isLoadingStandardFeatures}
                                            name={`standardFeature`}
                                            id={`standardFeature`}
                                            label="Standard Feature"
                                            value={values.standardFeature}
                                            error={Boolean(touched.standardFeature && errors.standardFeature)}
                                            helperText={touched.standardFeature && errors.standardFeature}
                                        >
                                            {standardFeatures &&
                                                standardFeatures.map((type: IDropdownResponse, index) => {
                                                    return (
                                                        <MenuItem key={`${index}`} value={type.value}>
                                                            {type.description}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </SMBSelect>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SMBSelect
                                            variant="outlined"
                                            required
                                            onChange={e => {
                                                setFieldValue('standardFeatureSelection', e.target.value);
                                            }}
                                            handleClear={() => {
                                                setFieldValue('standardFeatureSelection', '');
                                            }}
                                            onBlur={handleBlur}
                                            disabled={isLoadingStandardFeatures || isLoadingStandardFeatureSelection}
                                            name={`standardFeatureSelection`}
                                            id={`standardFeatureSelection`}
                                            label="Standard Feature Selection"
                                            value={values.standardFeatureSelection}
                                            error={Boolean(touched.standardFeatureSelection && errors.standardFeatureSelection)}
                                            helperText={touched.standardFeatureSelection && errors.standardFeatureSelection}
                                        >
                                            {standardFeatureSelections &&
                                                standardFeatureSelections.map((type: IDropdownResponse, index) => {
                                                    return (
                                                        <MenuItem key={`${index}`} value={type.value}>
                                                            {type.description}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </SMBSelect>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <h2>Select Neighborhoods</h2>
                                        <TextField
                                            fullWidth
                                            onChange={e => {
                                                setNeighborhoodFilter(e.target.value.trim());
                                            }}
                                            disabled={isLoadingNeighborhoods}
                                            value={neighborhoodFilter}
                                            name={`neighborhoodFilter`}
                                            id={`neighborhoodFilter`}
                                            label="Neighborhood Filter"
                                            size="small"
                                            helperText='(Optional) Enter text to filter the list of neighborhoods'
                                        />
                                        <Divider sx={{ mb: 2 }} />
                                        <Box mb={1}>
                                            {filteredNeighborhoods && filteredNeighborhoods.length !== 0 && (
                                                <CheckboxListTable
                                                    data={filteredNeighborhoods}
                                                    isLoading={isLoadingNeighborhoods}
                                                    selectedItems={selectedNeighborhoods}
                                                    setSelectedItems={setSelectedNeighborhoods}
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <h2>Select Plan Styles</h2>
                                        <Box mb={1}>
                                            {planStyles && planStyles.length !== 0 && (
                                                <CheckboxListTable
                                                    data={planStyles}
                                                    isLoading={isLoadingPlanStyles}
                                                    selectedItems={selectedPlanStyles}
                                                    setSelectedItems={setSelectedPlanStyles}
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Box mt={1}>
                            <Button
                                disabled={!dirty || isSubmitting || !isValid || isLoadingNeighborhoods || isLoadingPlanStyles || isLoadingStandardFeatureSelection}
                                type="submit"
                                startIcon={<Save />}
                                variant="contained"
                                color="primary"
                            >
                                Submit
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};